import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuditEntry, PlatformAccount, Site, TenantDetail } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { formatDateTime } from '../../shared/format';
import { useSession } from '../../shared/session';
import {
  ActionsCell,
  AdminIcon,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Empty,
  Field,
  Input,
  Modal,
  Note,
  SearchField,
  Table,
  Textarea,
  Toolbar,
} from '../admin-kit';
import { wanneer } from './platform-format';

/**
 * De tabbladen van een organisatie die Master toevoegt (MST-2): Mensen, Vestigingen, Auditspoor en
 * Gevarenzone. Een eigen bestand naast `tenant-detail-page.tsx`, dat al lang genoeg is.
 *
 * Functioneel gebouwd met de admin-kit; het ontwerp volgt (zie `Herbouwplan/12_Ontwerpbrief_Master.md`).
 */

/** Een fout van de API als tekst: de `detail` van een problem-antwoord, anders de algemene zin. */
function problemText(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown } | null)?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

// --- Mensen ------------------------------------------------------------------------------------

export function PeopleTab({
  tenantId,
  onToast,
}: {
  tenantId: string;
  onToast: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [zoek, setZoek] = useState('');

  const accounts = useQuery({
    queryKey: ['platform', 'accounts', tenantId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants/{tenantId}/accounts', {
        params: { path: { tenantId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: PlatformAccount[] }).items;
    },
  });

  const action = useMutation({
    mutationFn: async ({
      accountId,
      kind,
    }: {
      accountId: string;
      kind: 'block' | 'unblock' | 'password-reset';
    }) => {
      const params = { path: { tenantId, accountId } };
      const { error } =
        kind === 'block'
          ? await api.POST('/api/v1/platform/tenants/{tenantId}/accounts/{accountId}/block', {
              params,
            })
          : kind === 'unblock'
            ? await api.POST('/api/v1/platform/tenants/{tenantId}/accounts/{accountId}/unblock', {
                params,
              })
            : await api.POST(
                '/api/v1/platform/tenants/{tenantId}/accounts/{accountId}/password-reset',
                { params },
              );
      if (error) throw error;
      return kind;
    },
    onSuccess: async (kind) => {
      onToast(t(`master.people.done.${kind === 'password-reset' ? 'reset' : kind}`));
      await queryClient.invalidateQueries({ queryKey: ['platform', 'accounts', tenantId] });
    },
  });

  const term = zoek.trim().toLowerCase();
  const rows = (accounts.data ?? []).filter(
    (account) =>
      !term ||
      account.displayName.toLowerCase().includes(term) ||
      (account.loginEmail ?? '').toLowerCase().includes(term) ||
      (account.employeeNumber ?? '').toLowerCase().includes(term),
  );

  return (
    <Card>
      <CardHead title={t('master.people.title')} count={accounts.data?.length} />
      <Toolbar>
        <SearchField label={t('master.people.search')} value={zoek} onChange={setZoek} />
      </Toolbar>
      <CardPad>
        <p className="a-muted">{t('master.people.intro')}</p>
      </CardPad>
      {action.isError ? (
        <CardPad>
          <Note tone="danger" role="alert">
            {problemText(action.error, t('errors.generic'))}
          </Note>
        </CardPad>
      ) : null}
      {accounts.isPending ? (
        <LoadingState rows={4} label={t('common.loading')} />
      ) : accounts.isError ? (
        <CardPad>
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        </CardPad>
      ) : rows.length === 0 ? (
        <Empty icon={<AdminIcon name="people" size={24} />} title={t('master.people.empty')} />
      ) : (
        <Table label={t('master.people.title')}>
          <thead>
            <tr>
              <th>{t('master.people.name')}</th>
              <th>{t('master.people.login')}</th>
              <th>{t('master.people.status')}</th>
              <th>{t('master.people.lastLogin')}</th>
              <th className="act">
                <span className="a-sr">{t('master.people.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((account) => (
              <tr key={account.id} data-testid="master-account-row">
                <td>
                  <span className="a-name">{account.displayName}</span>
                </td>
                <td>
                  {account.loginEmail ?? (
                    <span className="a-mono">
                      {t('master.people.employeeNumber', { number: account.employeeNumber ?? '—' })}
                    </span>
                  )}
                </td>
                <td>
                  <AccountBadge status={account.status} />
                </td>
                <td>{wanneer(account.lastLoginAt, t)}</td>
                <ActionsCell>
                  {account.loginEmail && account.status !== 'disabled' ? (
                    <Btn
                      variant="ghost"
                      size="sm"
                      disabled={action.isPending}
                      onClick={() =>
                        action.mutate({ accountId: account.id, kind: 'password-reset' })
                      }
                    >
                      {t('master.people.reset')}
                    </Btn>
                  ) : null}
                  {account.status === 'disabled' ? (
                    <Btn
                      variant="ghost"
                      size="sm"
                      disabled={action.isPending}
                      onClick={() => action.mutate({ accountId: account.id, kind: 'unblock' })}
                    >
                      {t('master.people.unblock')}
                    </Btn>
                  ) : (
                    <Btn
                      variant="ghost"
                      size="sm"
                      testId="master-account-block"
                      disabled={action.isPending}
                      onClick={() => action.mutate({ accountId: account.id, kind: 'block' })}
                    >
                      {t('master.people.block')}
                    </Btn>
                  )}
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function AccountBadge({ status }: { status: PlatformAccount['status'] }) {
  const { t } = useTranslation();
  if (status === 'active') return <Badge tone="ok">{t('master.people.statusActive')}</Badge>;
  if (status === 'disabled') return <Badge tone="danger">{t('master.people.statusBlocked')}</Badge>;
  return <Badge tone="attention">{t('master.people.statusPending')}</Badge>;
}

// --- Vestigingen -------------------------------------------------------------------------------

export function SitesTab({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { api } = useSession();

  const sites = useQuery({
    queryKey: ['platform', 'sites', tenantId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants/{tenantId}/sites', {
        params: { path: { tenantId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: Site[] }).items;
    },
  });

  return (
    <Card>
      <CardHead title={t('master.sites.title')} count={sites.data?.length} />
      <CardPad>
        <p className="a-muted">{t('master.sites.intro')}</p>
      </CardPad>
      {sites.isPending ? (
        <LoadingState rows={2} label={t('common.loading')} />
      ) : sites.isError ? (
        <CardPad>
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        </CardPad>
      ) : (sites.data?.length ?? 0) === 0 ? (
        <Empty icon={<AdminIcon name="building" size={24} />} title={t('master.sites.empty')} />
      ) : (
        <Table label={t('master.sites.title')}>
          <thead>
            <tr>
              <th>{t('master.sites.name')}</th>
              <th>{t('master.sites.code')}</th>
              <th>{t('master.sites.address')}</th>
              <th>{t('master.sites.status')}</th>
            </tr>
          </thead>
          <tbody>
            {sites.data?.map((site) => (
              <tr key={site.id}>
                <td>
                  <span className="a-name">{site.name}</span>
                </td>
                <td className="a-mono">{site.code}</td>
                <td>{site.address || '—'}</td>
                <td>
                  {site.status === 'active' ? (
                    <Badge tone="ok">{t('master.sites.open')}</Badge>
                  ) : (
                    <Badge>{t('master.sites.closed')}</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// --- Auditspoor --------------------------------------------------------------------------------

/**
 * Het spoor van de organisatie zelf, zoals haar beheer het ziet. De laatste vijftig regels; wie
 * verder terug wil, filtert in het platformspoor (`/master/audit`).
 */
export function TenantAuditTab({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { api } = useSession();

  const trail = useQuery({
    queryKey: ['platform', 'tenant-audit', tenantId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/audit/tenants/{tenantId}', {
        params: { path: { tenantId }, query: { limit: 50 } as never },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: AuditEntry[] }).items;
    },
  });

  return (
    <Card>
      <CardHead title={t('master.audit.title')} />
      <CardPad>
        <p className="a-muted">{t('master.audit.intro')}</p>
      </CardPad>
      {trail.isPending ? (
        <LoadingState rows={4} label={t('common.loading')} />
      ) : trail.isError ? (
        <CardPad>
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        </CardPad>
      ) : (trail.data?.length ?? 0) === 0 ? (
        <Empty icon={<AdminIcon name="history" size={24} />} title={t('master.audit.empty')} />
      ) : (
        <Table label={t('master.audit.title')}>
          <thead>
            <tr>
              <th>{t('master.audit.when')}</th>
              <th>{t('master.audit.who')}</th>
              <th>{t('master.audit.what')}</th>
            </tr>
          </thead>
          <tbody>
            {trail.data?.map((entry) => (
              <tr key={entry.id}>
                <td>{formatDateTime(entry.occurredAt)}</td>
                <td>
                  {entry.actorType === 'platform_admin' ? (
                    <Badge tone="info">{entry.actorName}</Badge>
                  ) : (
                    entry.actorName
                  )}
                </td>
                <td>{entry.summary}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// --- Gevarenzone -------------------------------------------------------------------------------

/**
 * Archiveren (omkeerbaar) en definitief wissen (niet omkeerbaar), in die volgorde: wissen kan alleen
 * na archiveren, en vraagt de code van de organisatie.
 */
export function DangerZoneTab({
  tenant,
  onChanged,
  onToast,
}: {
  tenant: TenantDetail;
  onChanged: () => Promise<void>;
  onToast: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const tenantId = tenant.id;
  const archived = tenant.status === 'archived';

  const archive = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/platform/tenants/{tenantId}/archive', {
        params: { path: { tenantId } },
        body: { reason: reason.trim() } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setArchiveOpen(false);
      setReason('');
      onToast(t('master.danger.archived'));
      await onChanged();
    },
  });

  const restore = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/platform/tenants/{tenantId}/reactivate', {
        params: { path: { tenantId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      onToast(t('master.danger.restored'));
      await onChanged();
    },
  });

  const purge = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/platform/tenants/{tenantId}/purge', {
        params: { path: { tenantId } },
        body: { confirmCode: code.trim() } as never,
      });
      if (error || !data) throw error ?? new Error('Wissen mislukt');
      return data as { name: string; rows: number };
    },
    onSuccess: async () => {
      setPurgeOpen(false);
      // Eerst weg van de pagina: `onChanged` haalde het gewiste bedrijf opnieuw op en toonde
      // een foutmelding voor de navigatie. Daarna zijn queries weg, en dan pas de lijst.
      await navigate({ to: '/master/organisaties' });
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] === 'platform' && query.queryKey.includes(tenantId),
      });
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
  });

  return (
    <>
      <Card testId="master-danger-archive">
        <CardHead title={t('master.danger.archiveTitle')} />
        <CardPad className="a-stack">
          <p>{t('master.danger.archiveText')}</p>
          {archived ? (
            <>
              <Note tone="attention">
                {t('master.danger.isArchived', {
                  date: tenant.suspendedAt ? formatDateTime(tenant.suspendedAt) : '—',
                })}
                {tenant.suspendedReason ? ` — ${tenant.suspendedReason}` : ''}
              </Note>
              <div>
                <Btn variant="sec" disabled={restore.isPending} onClick={() => restore.mutate()}>
                  {t('master.danger.restore')}
                </Btn>
              </div>
            </>
          ) : (
            <div>
              <Btn variant="danger-sec" onClick={() => setArchiveOpen(true)}>
                {t('master.danger.archive')}
              </Btn>
            </div>
          )}
        </CardPad>
      </Card>

      <Card testId="master-danger-purge">
        <CardHead title={t('master.danger.purgeTitle')} />
        <CardPad className="a-stack">
          <p>{t('master.danger.purgeText')}</p>
          {archived ? null : <Note tone="info">{t('master.danger.purgeFirstArchive')}</Note>}
          <div>
            <Btn
              variant="danger"
              disabled={!archived}
              onClick={() => {
                setCode('');
                purge.reset();
                setPurgeOpen(true);
              }}
            >
              {t('master.danger.purge')}
            </Btn>
          </div>
        </CardPad>
      </Card>

      <Modal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        title={t('master.danger.archiveTitle')}
        sub={t('master.danger.archiveExplain')}
        footer={
          <>
            <Btn variant="sec" onClick={() => setArchiveOpen(false)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              variant="danger"
              disabled={reason.trim().length < 3 || archive.isPending}
              onClick={() => archive.mutate()}
            >
              {t('master.danger.archive')}
            </Btn>
          </>
        }
      >
        <Field
          label={t('master.danger.reason')}
          error={archive.isError ? t('errors.generic') : undefined}
        >
          {(props) => (
            <Textarea
              {...props}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={purgeOpen}
        onClose={() => setPurgeOpen(false)}
        title={t('master.danger.purgeTitle')}
        sub={t('master.danger.purgeExplain', { name: tenant.name })}
        footer={
          <>
            <Btn variant="sec" onClick={() => setPurgeOpen(false)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              variant="danger"
              testId="master-purge-confirm"
              disabled={code.trim().toLowerCase() !== tenant.code.toLowerCase() || purge.isPending}
              onClick={() => purge.mutate()}
            >
              {t('master.danger.purgeConfirm')}
            </Btn>
          </>
        }
      >
        <Field
          label={t('master.danger.typeCode', { code: tenant.code })}
          error={purge.isError ? problemText(purge.error, t('errors.generic')) : undefined}
        >
          {(props) => (
            <Input
              {...props}
              value={code}
              autoComplete="off"
              onChange={(event) => setCode(event.target.value)}
            />
          )}
        </Field>
      </Modal>
    </>
  );
}
